import * as React from 'react';
import type { ReportFilterOption } from '../../../../MedicareProAPI/Entities/Reports/Agency/ReportFilterOption';
import type { ReportFilterOptionCollection } from '../../../../MedicareProAPI/Collections/Reports/Agency/ReportFilterOptionCollection';
import { REPORT_TYPE } from '../../../Enums/AGENCY_REPORT_TYPE';
import type { FilterOption, ReportType } from '../../../Constants/AgencyReports';
import { FILTERABLE_FIELDS_BY_REPORT } from '../../../Constants/AgencyReports';
import { AgencyReportRepository } from '../../../../MedicareProAPI/Repositories/Reports/AgencyReportRepository';

interface ReportBuilderProps {
  reportType: ReportType;
  onReportFilterSelect: (report: FilterOption) => void;
  onRunReport: (
    userid: number,
    agencyId: number,
    reportName: string,
    criteria: any,
    offset: number,
    limit: number
  ) => void;
  onExport: (userid: number, agencyId: number, reportName: string, criteria: any) => void;
  onInheritChange: (inherit: boolean, agencyId: number) => void;
  isFetchingResults: boolean;
  isExporting: boolean;
  userId: number;
  agencyId: number;
  userAccessToken: string;
  contractsInherited: boolean;
  absoluteUplineId?: number;
}

/**
 * Component for building agency report with filters
 *
 * @component
 * @author Andrew Brown <abrown@medicareprocrm.com>
 * @date Created: 2025-06-16
 * @lastUpdated
 * @version 1.1.0
 */

export const ReportBuilder: React.FC<ReportBuilderProps> = ({
  reportType,
  onReportFilterSelect,
  onRunReport,
  onExport,
  onInheritChange,
  isFetchingResults,
  isExporting,
  userId,
  agencyId,
  userAccessToken,
  contractsInherited,
  absoluteUplineId,
}) => {
  const agencyReportRepository = new AgencyReportRepository();
  agencyReportRepository.setAccessToken(userAccessToken);

  const filterList = FILTERABLE_FIELDS_BY_REPORT[reportType.id];
  const [filterSelected, setFilterSelected] = React.useState<FilterOption | null>(null);
  const [selectedFilters, setSelectedFilters] = React.useState<string[] | number[]>([]);
  const [isFetchingFilterOptions, setIsFetchingFilterOptions] = React.useState<boolean>(false);
  const [filterOptions, setFilterOptions] = React.useState<Array<ReportFilterOption>>([]);

  const [dateFrom, setDateFrom] = React.useState<string>(null);
  const [dateTo, setDateTo] = React.useState<string>(null);
  const [rtsYear, setRtsYear] = React.useState<number>(new Date().getFullYear());
  const [rtsStatus, setRtsStatus] = React.useState<string>('All');

  const [shouldShowDates, setShouldShowDates] = React.useState<boolean>(false);
  const [isAllFilter, setIsAllFilter] = React.useState<boolean>(false);

  const hasAbsolute = absoluteUplineId !== agencyId;

  /**
   * Triggers the “Run Report” flow for the selected report.
   *
   * @param userId - ID of the user requesting the report
   * @param agencyId - ID of the agency context
   * @param reportName - Key or slug of the report to run
   * @param offset - Result offset for pagination
   * @param limit - Page size / max rows to fetch
   * @returns void
   */
  const handleRunReport = (
    userId: number,
    agencyId: number,
    reportName: string,
    offset: number,
    limit: number
  ): void => {
    const criteria = buildCriteria();
    onRunReport(userId, agencyId, reportName, criteria, offset, limit);
  };

  /**
   * Handles running the export process for a report.
   *
   * @param userId - The ID of the user requesting the export
   * @param agencyId - The ID of the agency context
   * @param reportName - The key or identifier of the report
   * @returns void
   */
  const handleRunExport = (userId: number, agencyId: number, reportName: string): void => {
    const criteria = buildCriteria();
    onExport(userId, agencyId, reportName, criteria);
  };

  /**
   * Builds a criteria object to be sent with the report request.
   * Only includes values that are set (non-empty filters or dates).
   */
  const buildCriteria = (): Record<string, any> => {
    const criteria: Record<string, any> = {};

    if (selectedFilters.length > 0) {
      criteria['filter1'] = selectedFilters;
    }
    if (dateFrom) {
      criteria['dateFrom'] = dateFrom;
    }
    if (dateTo) {
      criteria['dateTo'] = dateTo;
    }
    if (rtsYear) {
      criteria['rtsYear'] = rtsYear;
    }
    if (rtsStatus) {
      criteria['rtsStatus'] = rtsStatus;
    }

    return criteria;
  };

  /**
   * When a report type filter is set
   */
  React.useEffect(() => {
    if (filterSelected) {
      if (filterSelected.value.indexOf('_all') >= 0) {
        setIsAllFilter(true);
        setIsFetchingFilterOptions(false);
        onReportFilterSelect(filterSelected);
      } else {
        setIsAllFilter(false);
        setIsFetchingFilterOptions(true);
        agencyReportRepository
          .getReportFilterOptions(userId, agencyId, filterSelected.value)
          .then((reportFilterOptions: ReportFilterOptionCollection) => {
            setFilterOptions(reportFilterOptions.getItems());
            setIsFetchingFilterOptions(false);
            onReportFilterSelect(filterSelected);
          });
      }
    }
  }, [filterSelected, userId, agencyId]);

  /**
   * When a report type is set
   */
  React.useEffect(() => {
    if (reportType.id == REPORT_TYPE.SALES) {
      setShouldShowDates(true);
    } else {
      setShouldShowDates(false);
    }
    setFilterSelected(null);
    setFilterOptions([]);
    setDateFrom('');
    setDateTo('');
  }, [reportType]);

  return (
    <div className="row mb-3">
      {/* Filter dropdown */}
      <div className="col-4">
        <label className="label">Filter By</label>
        <select
          className="form-control form-control-sm"
          value={filterSelected?.value || ''}
          onChange={(e) => {
            const value = e.target.value;
            const selected = filterList.find((opt) => opt.value === value) || null;
            setFilterSelected(selected);
            setSelectedFilters([]);
          }}
        >
          <option value="">Select...</option>
          {filterList.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Secondary options */}
      {filterSelected && !isAllFilter && (
        <div className="col-4">
          {isFetchingFilterOptions ? (
            <i className="far fa-spinner fa-spin"></i>
          ) : (
            <>
              <label className="label">Refine</label>
              <select
                className="form-control form-control-sm"
                multiple
                onChange={(e) => {
                  const values = Array.from(e.target.selectedOptions, (opt) => opt.value);
                  setSelectedFilters(values);
                }}
              >
                {filterOptions.map((option, i) => {
                  const key = option.getKey();
                  if (key === null || key === undefined) return null;

                  return (
                    <option key={i} value={key}>
                      {option.getField()}
                    </option>
                  );
                })}
              </select>
            </>
          )}
        </div>
      )}

      {/* Dates */}
      {filterSelected && shouldShowDates && (
        <>
          <div className="col-2">
            <label className="label">Policy Effective Start</label>
            <input
              type="text"
              className="form-control form-control-sm needsSlashes"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>
          <div className="col-2">
            <label className="label">Policy Effective End</label>
            <input
              type="text"
              className="form-control form-control-sm needsSlashes"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
        </>
      )}
      {reportType.id === REPORT_TYPE.RTS && (
        <div className="col-2">
          <label className="label">RTS Year</label>
          <select
            className="form-control form-control-sm"
            value={rtsYear}
            onChange={(e) => setRtsYear(parseInt(e.target.value))}
          >
            {[new Date().getFullYear(), new Date().getFullYear() + 1].map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      )}
      {reportType.id === REPORT_TYPE.RTS && (
        <div className="col-2">
          <label className="label">RTS Status</label>
          <select
            className="form-control form-control-sm"
            value={rtsStatus}
            onChange={(e) => setRtsStatus(e.target.value)}
          >
            <option value="All">All</option>
            <option value="RTS">RTS</option>
            <option value="NRTS">Not RTS</option>
          </select>
        </div>
      )}

      {/* Notification about inheriting contracts */}
      {hasAbsolute && contractsInherited && reportType.id === REPORT_TYPE.CONTRACTS && (
        <>
          <div className="col-12 mt-2">
            <div className="alert alert-info p-1" style={{ fontSize: '9pt' }}>
              <i className="far fa-exclamation-circle"></i> You currently inherit contract details
              from your absolute upline agency.{' '}
              <div className="badge badge-info cp" onClick={() => onInheritChange(false, agencyId)}>
                Disable
              </div>
            </div>
          </div>
        </>
      )}
      {hasAbsolute && !contractsInherited && reportType.id === REPORT_TYPE.CONTRACTS && (
        <>
          <div className="col-12 mt-2">
            <div className="alert alert-info p-1" style={{ fontSize: '9pt' }}>
              <i className="far fa-exclamation-circle"></i> You can also configure your agency
              platform to always inherit contract data from your upline agency.{' '}
              <div className="badge badge-info cp" onClick={() => onInheritChange(true, agencyId)}>
                Enable
              </div>
            </div>
          </div>
        </>
      )}

      {/* Actions */}
      {filterSelected &&
        (selectedFilters.length > 0 || isAllFilter) &&
        (!shouldShowDates || (dateFrom && dateTo)) && (
          <>
            <div className="col-12 mt-2">
              <button
                className="btn btn-sm btn-info"
                onClick={() => handleRunReport(userId, agencyId, filterSelected.value, 0, 25)}
                disabled={false}
              >
                {isFetchingResults ? (
                  <>
                    <i className="far fa-spinner fa-spin me-1"></i>
                  </>
                ) : (
                  'Run Report'
                )}
              </button>

              <button
                className="ml-2 btn btn-sm btn-success"
                onClick={() => handleRunExport(userId, agencyId, filterSelected.value)}
                disabled={isExporting}
              >
                {isExporting ? (
                  <>
                    <i className="far fa-spinner fa-spin me-1"></i>
                  </>
                ) : (
                  'Export CSV'
                )}
              </button>
            </div>
          </>
        )}
    </div>
  );
};
